import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserStore } from '@/store/user-store';
import { useGymStore } from '@/store/gym-store';
import { getCurrentUserApi } from '@/api/user';
import { getGymsApi } from '@/api/gym';
import { SplashScreen } from '@/components/splash-screen';
import { Button } from '@/components/ui/button';

interface AppProviderProps {
  children: React.ReactNode;
}

type SessionLoadState =
  | { status: 'idle' }
  | { status: 'loading'; token: string }
  | { status: 'ready'; token: string }
  | { status: 'error'; token: string }
  | { status: 'not-owner'; token: string };

/*
 * Session boot (PWA-safe):
 *
 *   hydrate
 *     no token  → idle → children
 *     token     → loadId++ → GET /me → GET /gyms
 *                    2xx  → ready
 *                    401  → interceptor refresh (same loadId; token rotation is not re-init)
 *                    fail → error → Retry = location.reload()
 *
 * accessToken: null→string login; string→null logout; string→string refresh (ignore).
 */

function isGymOwner(user: { role?: string; roles?: string[] } | null): boolean {
  if (!user) return false;
  if (user.roles?.includes('GYM_OWNER')) return true;
  return user.role === 'GYM_OWNER' && (!user.roles || user.roles.length === 0);
}

/**
 * Unified App Provider
 * Single source of truth for app initialization
 * - Waits for Zustand hydration
 * - Fetches user + gyms when authenticated
 * - Shows loader until session data is ready
 * - Handles redirects (onboarding, no-gyms)
 *
 * Gyms-based redirects only run after a *successful* gyms fetch for the
 * current access token. Without that gate, post-login races send owners who
 * already have gyms to /app/gym/new while `/v1/gym-owner/gyms` is still pending.
 */
export function AppProvider({ children }: AppProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const [sessionLoad, setSessionLoad] = useState<SessionLoadState>({ status: 'idle' });

  const {
    accessToken,
    setUser,
    _hasHydrated,
    user,
    isAuthenticated,
    isInitializing,
    setIsInitializing,
    clearAuth,
  } = useUserStore();

  const { setGyms, setLoading: setGymsLoading, gyms, selectedGymId, clearGyms } = useGymStore();

  const isFetchingRef = useRef(false);
  const sessionLoadRef = useRef(sessionLoad);
  const loadIdRef = useRef(0);
  const prevAccessTokenRef = useRef<string | null | undefined>(undefined);

  sessionLoadRef.current = sessionLoad;

  // Ready/error follow presence, not the access-token string (refresh rotates it).
  const isSessionDataReady = !!accessToken && sessionLoad.status === 'ready';
  const isSessionLoadError = !!accessToken && sessionLoad.status === 'error';
  const isNotOwner = !!accessToken && sessionLoad.status === 'not-owner';

  const initialize = useCallback(async () => {
    if (!accessToken) {
      isFetchingRef.current = false;
      setSessionLoad({ status: 'idle' });
      clearGyms();
      setIsInitializing(false);
      return;
    }

    const current = sessionLoadRef.current;
    if (current.status === 'ready' || current.status === 'not-owner') {
      setIsInitializing(false);
      return;
    }

    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    const loadId = ++loadIdRef.current;
    setSessionLoad({ status: 'loading', token: accessToken });
    setIsInitializing(true);
    setGymsLoading(true);

    const isCurrentLoad = () => loadIdRef.current === loadId;
    const currentToken = () => useUserStore.getState().accessToken ?? accessToken;

    try {
      const userData = await getCurrentUserApi();
      if (!isCurrentLoad()) return;
      setUser(userData);

      if (!isGymOwner(userData)) {
        setSessionLoad({ status: 'not-owner', token: currentToken() });
        return;
      }

      const gymsData = await getGymsApi();
      if (!isCurrentLoad()) return;
      setGyms(gymsData.gyms || []);
      setSessionLoad({ status: 'ready', token: currentToken() });
    } catch (error) {
      console.error('Initialization error:', error);
      if (!isCurrentLoad()) return;
      setSessionLoad({ status: 'error', token: currentToken() });
    } finally {
      if (isCurrentLoad()) {
        isFetchingRef.current = false;
        setGymsLoading(false);
        setIsInitializing(false);
      }
    }
  }, [accessToken, setUser, setGyms, setGymsLoading, setIsInitializing, clearGyms]);

  // Re-init after hydration and on login/logout. Access-token rotation is not a new session.
  useEffect(() => {
    if (!_hasHydrated) return;

    const prevToken = prevAccessTokenRef.current;
    const presenceChanged = Boolean(prevToken) !== Boolean(accessToken);

    if (prevToken !== accessToken) {
      if (prevToken !== undefined && presenceChanged) {
        isFetchingRef.current = false;
        loadIdRef.current += 1;
        sessionLoadRef.current = { status: 'idle' };
        setSessionLoad({ status: 'idle' });
        if (accessToken) {
          setIsInitializing(true);
        }
      }
      prevAccessTokenRef.current = accessToken;
    }

    if (prevToken !== undefined && !presenceChanged) {
      return;
    }

    void initialize();
  }, [_hasHydrated, accessToken, initialize, setIsInitializing]);

  // Onboarding redirect
  useEffect(() => {
    if (!_hasHydrated || isInitializing || !user) return;

    if (user.onboardingComplete === false) {
      if (!location.pathname.startsWith('/user/onboarding')) {
        navigate('/user/onboarding', { replace: true });
      }
    }
  }, [_hasHydrated, isInitializing, user, location.pathname, navigate]);

  // No-gyms redirect — only after gyms loaded successfully for the current token
  useEffect(() => {
    if (!_hasHydrated || isInitializing || !isAuthenticated || !user || !accessToken) return;
    if (!isSessionDataReady) return;
    if (user.onboardingComplete === false) return;

    const isNewGymRoute = location.pathname.startsWith('/app/gym/new');
    const isAuthRoute = location.pathname.startsWith('/auth');
    const isOnboardingRoute = location.pathname.startsWith('/user/onboarding');
    const isGymFilesRoute = location.pathname.includes('/files');

    if (
      !isNewGymRoute &&
      !isAuthRoute &&
      !isOnboardingRoute &&
      !isGymFilesRoute &&
      gyms.length === 0
    ) {
      navigate('/app/gym/new', { replace: true });
    }
  }, [
    _hasHydrated,
    isInitializing,
    isAuthenticated,
    user,
    accessToken,
    isSessionDataReady,
    gyms.length,
    location.pathname,
    navigate,
  ]);

  // Root /app → last selected gym dashboard
  useEffect(() => {
    if (!_hasHydrated || isInitializing || !isAuthenticated || !user || !accessToken) return;
    if (!isSessionDataReady) return;
    if (user.onboardingComplete === false || gyms.length === 0) return;

    const isRootAppRoute = location.pathname === '/app' || location.pathname === '/app/';

    if (isRootAppRoute && selectedGymId) {
      navigate(`/app/${selectedGymId}/dashboard`, { replace: true });
    }
  }, [
    _hasHydrated,
    isInitializing,
    isAuthenticated,
    user,
    accessToken,
    isSessionDataReady,
    gyms.length,
    selectedGymId,
    location.pathname,
    navigate,
  ]);

  const handleRetrySessionLoad = () => {
    window.location.reload();
  };

  // Hydrating or loading session data
  if (
    !_hasHydrated ||
    isInitializing ||
    (isAuthenticated && accessToken && !isSessionDataReady && !isSessionLoadError && !isNotOwner)
  ) {
    return <SplashScreen />;
  }

  if (isNotOwner) {
    return (
      <div className="min-h-screen bg-surface-base flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-body-medium text-foreground-primary">
          This email is a customer account. Create a gym-owner signup with the same email and
          password to manage a gym.
        </p>
        <Button
          variant="primary"
          onClick={() => {
            clearAuth();
            navigate('/auth/signup');
          }}
          className="rounded-lg"
        >
          Continue as gym owner
        </Button>
      </div>
    );
  }

  // Failed to load user/gyms after auth — offer retry instead of false no-gym redirect
  if (isSessionLoadError) {
    return (
      <div className="min-h-screen bg-surface-base flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-body-medium text-foreground-primary">
          We couldn&apos;t load your gym data. Please try again.
        </p>
        <Button variant="primary" onClick={handleRetrySessionLoad} className="rounded-lg">
          Retry
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}
